import React, { useEffect } from "react";
import { useState } from "react";
import DataContext from "./DataContext";
import {
  QualityLineProps,
  OrderProps,
  ReportProps,
  RoleProps,
  UserDataProps,
  ReportMachinistProps,
  RigProps,
} from "../interfaces/interfaces";
import type { TablePaginationConfig } from "antd/es/table";
import type { FilterValue } from "antd/es/table/interface";
import exportFromJSON from "export-from-json";
import { useRequest } from "../hooks";
import dayjs from "dayjs";
import { useNotification } from "./NotificationProvider";

interface Props {
  children: JSX.Element | JSX.Element[];
}
interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: string;
  sortOrder?: string;
  filters?: Record<string, FilterValue>;
}

interface FilterProps {
  machinist?: string[] | null;
  startDate?: string | null;
  endDate?: string | null;
}

const DataProvider = ({ children }: Props) => {
  const { handleRequest } = useRequest();
  const [isLoading, setIsLoading] = useState(false);
  const { showNotification } = useNotification();
  const [machinistReportData, setmachinistReportData] = useState<
    ReportMachinistProps[]
  >([]);
  const [userList, setUserList] = useState<UserDataProps[]>([]);
  const [orderData, setOrderData] = useState<OrderProps[]>([]);
  const [userData, setUserData] = useState<UserDataProps[]>([]);
  const [roleData, setRoleData] = useState<RoleProps[]>([]);
  const [orderToEdit, setOrderToEdit] = useState<OrderProps | undefined>();
  const [drillingCompanies, setDrillingCompanies] = useState<
    { text: string; value: string }[]
  >([]);
  const [rigNumbers, setRigNumbers] = useState<RigProps[]>([]);
  const [orderLineToEdit, setOrderLineToEdit] = useState<
    QualityLineProps | undefined
  >();
  const [roleToEdit, setRoleToEdit] = useState<RoleProps | undefined>();
  const [userToEdit, setUserToEdit] = useState<UserDataProps | undefined>();
  const [selectedData, setSelectedData] = useState<any[]>([]);
  const [dataTableSize, setTableSize] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      total: 100,
      onChange: (page, pageSize) => {
        setTableSize(pageSize);
        setPage(page);
        const query = localStorage.getItem("query") || undefined;
        switch (window.location.pathname) {
          case "/order":
            fetchOrderData(page, pageSize, query);
            break;
          default:
            break;
        }
      },
    },
  });

  useEffect(() => {
    if (searchQuery) {
      localStorage.setItem("query", searchQuery);
    } else {
      localStorage.removeItem("query");
    }
  }, [searchQuery]);

  // utils

  const handlePagination = (pagination: any) => {
    if (pagination) {
      setTableParams((prev) => {
        return {
          ...prev,
          pagination: {
            ...prev.pagination,
            total: parseInt(pagination.total),
          },
        };
      });
    }
  };

  const handleMultipleSelect = <T,>(data: any[], selectedRows: React.Key[]) => {
    const result = data.filter((item) => selectedRows.includes(item.OID));
    setSelectedData(result);
  };

  const handleExportExcel = (type: "order" | "item") => {
    try {
      setIsLoading(true);

      let fileName = type + "_status_" + dayjs(new Date());
      let data = selectedData.map((item) => {
        type === "order" && delete item.QualityLines && delete item.OrderId;
        delete item.OptimisticLockField;
        delete item.GCRecord;
        return item;
      });
      exportFromJSON({ data, fileName, exportType: "csv" });
      setSelectedData([]);
      setIsLoading(false);
    } catch {}
  };

  const handleRemoveSelectedData = (endpoint: string) => {
    if (selectedData.length === 1) {
      let options: RequestInit = {
        method: "DELETE",
        body: JSON.stringify({ itemsToDelete: selectedData }),
      };
      handleRequest<OrderProps>({
        endpoint: `${endpoint}/`,
        options,
        onSuccess: () => fetchData(endpoint),
        onError: (error) => console.log(error),
      });
    }
  };

  const handleRemoveUser = (idUser: string) => {
    let options: RequestInit = {
      method: "DELETE",
      body: JSON.stringify({ itemsToDelete: [idUser] }),
    };
    handleRequest<OrderProps>({
      endpoint: `users/${idUser}`,
      options,
      onSuccess: () => {
        showNotification("success", "Removed", "User Removed.");
        fetchUserData();
        fetchUserList();
      },
      onError: (error) => console.log(error),
    });
  };

  const handleSearch = (value: string) => {
    setPage(1);
    setTableSize(10);
    switch (window.location.pathname) {
      case "/order":
        fetchOrderData(1, 10, value);
        break;
      default:
        break;
    }
  };

  // Fetch

  const fetchData = (endpoint: string) => {
    setSelectedData([]);
    switch (endpoint) {
      case "orders":
        return fetchOrderData(page, dataTableSize);
      default:
        break;
    }
  };

  const fetchDataReport = ({
    machinist = null,
    startDate = null,
    endDate = null,
  }: FilterProps) => {
    setIsLoading(true);
    let options: RequestInit = {
      method: "POST",
      body: JSON.stringify({
        machinist,
        drillingCompanies,
        endDate,
      }),
    };
    handleRequest<ReportProps>({
      endpoint: "orders/report",
      options,
      onSuccess: (response) => {
        setIsLoading(false);
      },
      onError: () => {
        setIsLoading(false);
      },
    });
  };

  const fetchOrderLineReport = ({
    machinist = null,
    startDate = null,
    endDate = null,
  }: {
    machinist: string | null;
    startDate: string | null;
    endDate: string | null;
  }) => {
    setIsLoading(true);
    let options: RequestInit = {
      method: "POST",
      body: JSON.stringify({
        machinist,
        startDate,
        endDate,
      }),
    };
    handleRequest<ReportProps>({
      endpoint: "order-lines/report",
      options,
      onSuccess: (response) => {
        setmachinistReportData(response.data);
        setIsLoading(false);
      },
      onError: () => {
        showNotification(
          "error",
          "Something went wrong",
          "Please try again later",
        );
        setIsLoading(false);
      },
    });
  };

  const fetchOrderData = (
    page?: number,
    pageSize?: number,
    search?: string,
  ) => {
    setIsLoading(true);
    let options: RequestInit = {
      method: "GET",
    };
    let searchEncoded = search && encodeURIComponent(search);
    const endpoint =
      page && pageSize && searchEncoded
        ? `orders/?page=${page}&perPage=${pageSize}&search=${searchEncoded}`
        : page && pageSize && !searchEncoded
          ? `orders/?page=${page}&perPage=${pageSize}`
          : !page && !pageSize && searchEncoded
            ? `orders/?search=${searchEncoded}`
            : "orders";
    handleRequest<OrderProps>({
      endpoint,
      options,
      onSuccess: (response, pagination) => {
        handlePagination(pagination);
        setOrderData(response.data);
        setIsLoading(false);
      },
      onError: () => {
        showNotification(
          "error",
          "Something went wrong",
          "Please try again later",
        );
        setIsLoading(false);
      },
    });
  };

  const fetchRoleData = () => {
    setIsLoading(true);
    let options: RequestInit = {
      method: "GET",
    };
    handleRequest<RoleProps>({
      endpoint: "roles",
      options,
      onSuccess: (response) => {
        handlePagination({
          current: 0,
          totalPages: 1,
          total: response.data.length,
        });
        setRoleData(response.data);
        setIsLoading(false);
      },
      onError: () => {
        setIsLoading(false);
      },
    });
  };

  const fetchUserData = () => {
    setIsLoading(true);
    let options: RequestInit = {
      method: "GET",
    };
    handleRequest<UserDataProps>({
      endpoint: "users",
      options,
      onSuccess: (response) => {
        handlePagination({
          current: 0,
          totalPages: 1,
          total: response.data?.length,
        });
        setUserData(response.data);
        setIsLoading(false);
      },
      onError: () => {
        setIsLoading(false);
      },
    });
  };
  const fetchUserList = () => {
    setIsLoading(true);
    let options: RequestInit = {
      method: "GET",
    };
    handleRequest<UserDataProps>({
      endpoint: "users/all",
      options,
      onSuccess: (response) => {
        setUserList(response.data);
        setIsLoading(false);
      },
      onError: () => {
        setIsLoading(false);
      },
    });
  };

  const fetchRigNumbers = () => {
    let options: RequestInit = {
      method: "GET",
    };
    handleRequest<RigProps>({
      endpoint: "rig-number/all",
      options,
      onSuccess: (response) => {
        setRigNumbers(response.data);
      },
    });
  };

  // Create

  const handleCreateOrder = (order: OrderProps) => {
    let options: RequestInit = {
      method: "POST",
      body: JSON.stringify(order),
    };
    handleRequest<OrderProps>({
      endpoint: "orders/",
      options,
      onSuccess: () => {
        showNotification("success", "Saved", "Order saved.");
        fetchOrderData(page, dataTableSize);
      },
      onError: (error) => {
        console.log(error);
        showNotification(
          "error",
          "Something went wrong",
          error?.message || "Please try again later",
        );
      },
    });
  };

  const handleCreateRig = (RigName: string) => {
    let options: RequestInit = {
      method: "POST",
      body: JSON.stringify({ Name: RigName }),
    };
    handleRequest<OrderProps>({
      endpoint: "rig-number/",
      options,
      onSuccess: () => {
        showNotification("success", "Saved", "Rig Number Created.");
        fetchRigNumbers();
      },
      onError: (error) => {
        console.log(error);
        showNotification(
          "error",
          "Something went wrong",
          error?.message || "Please try again later",
        );
      },
    });
  };

  const handleCreateUser = (user: UserDataProps) => {
    let options: RequestInit = {
      method: "POST",
      body: JSON.stringify(user),
    };
    handleRequest<UserDataProps>({
      endpoint: "users/",
      options,
      onSuccess: () => {
        showNotification("success", "Saved", "User saved.");
        fetchUserData();
        fetchUserList();
      },
      onError: (error) => {
        console.log(error);
        showNotification(
          "error",
          "Something went wrong",
          error?.message || "Please try again later",
        );
      },
    });
  };

  // Edit

  const handleEditOrder = (order: OrderProps) => {
    let options: RequestInit = {
      method: "PUT",
      body: JSON.stringify(order),
    };
    handleRequest<OrderProps>({
      endpoint: `orders/${order.OID}`,
      options,
      onSuccess: () => {
        showNotification("success", "Saved", "Order saved.");

        fetchOrderData(page, dataTableSize);
      },
      onError: (error) => console.log(error),
    });
  };

  const handleEditRole = (role: RoleProps) => {
    let options: RequestInit = {
      method: "PUT",
      body: JSON.stringify(role),
    };
    handleRequest<RoleProps>({
      endpoint: `roles/${role.Oid}`,
      options,
      onSuccess: () => fetchRoleData(),
      onError: (error) => console.log(error),
    });
  };

  const handleEditUser = (user: UserDataProps) => {
    let options: RequestInit = {
      method: "PUT",
      body: JSON.stringify(user),
    };
    handleRequest<UserDataProps>({
      endpoint: `users/${user.OID}`,
      options,
      onSuccess: () => {
        showNotification("success", "Saved", "User saved.");
        fetchUserData();
      },
      onError: (error) => {
        showNotification(
          "error",
          "Something went wrong",
          "Please try again later",
        );
      },
    });
  };

  return (
    <DataContext.Provider
      value={{
        isLoading,
        tableParams,
        dataTableSize,
        page,
        machinistReportData,
        orderData,
        roleData,
        userData,
        orderToEdit,
        orderLineToEdit,
        roleToEdit,
        userToEdit,
        rigNumbers,
        drillingCompanies,
        selectedData,
        searchQuery,
        userList,
        onChangeSearchQuery: (newValue: string) => setSearchQuery(newValue),
        handleSearch,
        fetchDataReport,
        fetchOrderData,
        fetchRoleData,
        fetchUserData,
        fetchUserList,
        fetchRigNumbers,
        fetchOrderLineReport,
        onEditOrder: setOrderToEdit,
        onEditOrderLine: setOrderLineToEdit,
        onEditRole: setRoleToEdit,
        onEditUser: setUserToEdit,
        onMultipleSelect: handleMultipleSelect,
        onExportExcel: handleExportExcel,
        handleEditOrder,
        handleCreateOrder,
        handleEditRole,
        handleCreateUser,
        handleCreateRig,
        handleEditUser,
        handleRemoveSelectedData,
        handleRemoveUser,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

export default DataProvider;
